import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Form,
  FormGroup,
  FormSelect,
  FormSelectOption,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import type { Certificate } from '../../../types/models/Certificate';
import type { ApiResponseCertificateRevoke } from '../../../types/api/ApiResponseCertificate';

interface RevokeCertificateModalProps {
  certificate: Certificate | null;
  onClose: () => void;
  onRevoked: (updatedCertificate: Certificate) => void;
}

// Order matches RFC 5280 reason codes 0..6 and the OPNsense UI ordering.
// `value` is the on-the-wire lowercase snake_case; `label` is user-facing.
const CRL_REASONS = [
  { value: 'unspecified', label: 'Unspecified' },
  { value: 'key_compromise', label: 'Key Compromise' },
  { value: 'ca_compromise', label: 'CA Compromise' },
  { value: 'affiliation_changed', label: 'Affiliation Changed' },
  { value: 'superseded', label: 'Superseded' },
  { value: 'cessation_of_operation', label: 'Cessation of Operation' },
  { value: 'certificate_hold', label: 'Certificate Hold' },
] as const;

type CrlReasonValue = (typeof CRL_REASONS)[number]['value'];

// Defensive: the backend's `platformReason` should always be one of the seven
// CRL values, but a future driver might extend the set — fall back to the raw
// value rather than rendering an empty string.
function labelForReason(value: string): string {
  const match = CRL_REASONS.find((r) => r.value === value);
  return match?.label ?? value;
}

function RevokeCertificateModal({ certificate, onClose, onRevoked }: RevokeCertificateModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [selectedReason, setSelectedReason] = useState<CrlReasonValue | ''>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const isOpen = certificate !== null;

  useEffect(() => {
    if (isOpen) {
      setSelectedReason('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const sansMinusCn = useMemo(() => {
    if (certificate === null) {
      return [];
    }
    return certificate.sans.filter((s) => s !== certificate.cn);
  }, [certificate]);

  const handleSubmit = async () => {
    if (certificate === null || selectedReason === '') {
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await apiFetch(`/datacenters/certificates/${certificate.id}/revoke`, {
        method: 'POST',
        body: JSON.stringify({ reason: selectedReason }),
      });

      let body: ApiResponseCertificateRevoke | null;
      try {
        body = (await response.json()) as ApiResponseCertificateRevoke;
      } catch {
        body = null;
      }

      if (response.ok && body?.status === 'success' && body.data?.certificate) {
        // `platformReason` is authoritative — it correctly handles the
        // `alreadyRevoked` drift case where the platform's existing reason
        // differs from the user's request. Fall back to the user's pick only
        // if the backend omits it.
        const platformReasonLabel = labelForReason(body.data.platformReason ?? selectedReason);
        if (body.data.alreadyRevoked) {
          addNotification({
            title: 'Certificate already revoked',
            srTitle: 'Info alert',
            description: `${certificate.cn} was already revoked on the platform (reason: ${platformReasonLabel}). The local record has been synced.`,
            variant: 'info',
          });
        } else {
          addNotification({
            title: 'Certificate revoked',
            srTitle: 'Success alert',
            description: `${certificate.cn} has been revoked (reason: ${platformReasonLabel}).`,
            variant: 'success',
          });
        }
        setDrawerOpen(true);
        onRevoked(body.data.certificate);
        return;
      }

      const fallback =
        body?.data?.msg ??
        body?.message ??
        `Request failed with status ${response.status} ${response.statusText}`.trim();
      addNotification({
        title: 'Could not revoke certificate',
        srTitle: 'Error alert',
        description: fallback,
        variant: 'danger',
      });
      setDrawerOpen(true);
      onClose();
    } catch (err) {
      addNotification({
        title: 'Could not revoke certificate',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected network error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const cn = certificate?.cn ?? '';
  const title = certificate !== null ? `Revoke ${cn} certificate` : 'Revoke certificate';
  const ariaLabel = certificate !== null ? `Revoke certificate ${cn}` : 'Revoke certificate';

  return (
    <Modal variant="medium" isOpen={isOpen} onClose={onClose} aria-label={ariaLabel}>
      <ModalHeader title={title} />
      <ModalBody>
        {certificate !== null && (
          <>
            <Content className="pf-v6-u-mb-md">
              You are about to revoke the certificate <strong>{cn}</strong>. This action cannot be undone via the
              manager.
            </Content>
            <DescriptionList isCompact isHorizontal className="pf-v6-u-mb-md">
              <DescriptionListGroup>
                <DescriptionListTerm>Common name</DescriptionListTerm>
                <DescriptionListDescription>{cn}</DescriptionListDescription>
              </DescriptionListGroup>
              {sansMinusCn.length > 0 && (
                <DescriptionListGroup>
                  <DescriptionListTerm>SANs</DescriptionListTerm>
                  <DescriptionListDescription>{sansMinusCn.join(', ')}</DescriptionListDescription>
                </DescriptionListGroup>
              )}
            </DescriptionList>
            <Form>
              <FormGroup label="Revocation reason" isRequired fieldId="revoke-certificate-reason">
                <FormSelect
                  id="revoke-certificate-reason"
                  value={selectedReason}
                  onChange={(_event, value) => setSelectedReason(value as CrlReasonValue | '')}
                  aria-label="Revocation reason"
                  isRequired
                >
                  <FormSelectOption value="" label="Select a reason..." isDisabled />
                  {CRL_REASONS.map((r) => (
                    <FormSelectOption key={r.value} value={r.value} label={r.label} />
                  ))}
                </FormSelect>
              </FormGroup>
            </Form>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          variant="danger"
          onClick={() => void handleSubmit()}
          isLoading={isSubmitting}
          isDisabled={isSubmitting || selectedReason === '' || certificate === null}
        >
          Revoke certificate
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isSubmitting}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default RevokeCertificateModal;
